import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavShortcut } from 'src/components/AppHeader/types';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import { POSTHOG_EVENTS } from 'src/features/tracking/posthog-events';
import useSiteimprove from './use-siteimprove';

const TYPING_ROLES = ['textbox', 'combobox', 'searchbox'];

const isTypingElement = (element: Element | null | undefined): boolean => {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) return true;

  const role = element.getAttribute('role');
  return role !== null && TYPING_ROLES.includes(role);
};

const isInsideDialog = (element: Element | null | undefined): boolean =>
  element?.closest('dialog[open], [role="dialog"], [role="alertdialog"]') != null;

// event.code is used because Option+Shift+letter on macOS yields special characters in event.key.
const matchesShortcut = (event: KeyboardEvent, key: string): boolean =>
  event.altKey &&
  event.shiftKey &&
  !event.ctrlKey &&
  !event.metaKey &&
  event.code === `Key${key.toUpperCase()}`;

/**
 * Navigates to the given path when Alt+Shift+letter is pressed.
 * Shortcuts are ignored while the user is typing and when a dialog is open.
 */
export default function useNavShortcuts(shortcuts: NavShortcut[]) {
  const navigate = useNavigate();
  const location = useLocation();
  const { log } = useSiteimprove();

  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.repeat) return;

      const target = (event.target as Element | null) ?? document.activeElement;

      if (isTypingElement(target) || isTypingElement(document.activeElement)) return;
      if (
        isInsideDialog(target) ||
        document.querySelector('dialog[open], [role="dialog"], [role="alertdialog"]') !== null
      )
        return;

      const shortcut = shortcutsRef.current.find((item) => matchesShortcut(event, item.key));
      if (!shortcut) return;

      event.preventDefault();
      window.posthog?.capture(POSTHOG_EVENTS.NAV_SHORTCUT_USED, {
        key: shortcut.key,
        from_path: pathnameRef.current,
        to_path: shortcut.path,
      });
      navigate(shortcut.path);
      log(
        SITEIMPROVE_DECLARATIONS.NAVIGATION.id,
        shortcut.path,
        SITEIMPROVE_DECLARATIONS.NAVIGATION.labels.SHORTCUT,
      );
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [log, navigate]);
}
